<template>
  <BaseDrawer
    shrink
    :is-open="isOpen"
    @close="handleCloseDrawer"
  >
    <BaseInnerLoading v-if="isLoading" />
    <div
      v-else-if="isError"
      class="p-4 text-center text-error"
      >Something went wrong fetching email addresses</div
    >
    <PortalAlternateEmailInput
      v-else-if="isAlternateAddressInputVisible"
      @email-sent="handleEmailSent"
      @back-button-click="
        isAlternateAddressInputVisible = false
      "
    />
    <div
      v-else-if="emailAddresses"
      class="space-y-4 px-4 pb-4"
    >
      <p
        class="px-4 pt-4 text-center text-sm text-background-fg/70"
      >
        A link to access your web portal will be emailed to
        the selected email address
      </p>
      <ul
        class="flex max-h-[50vh] flex-col items-center space-y-2 overflow-y-auto overflow-x-hidden pr-2"
      >
        <li
          v-for="email in emailAddresses"
          :key="email"
          class="w-full"
        >
          <button
            class="relative flex w-full items-center rounded-lg border border-primary px-3 py-2 font-semibold text-background-fg/70"
            @click="requestLoginLink(email)"
          >
            <div class="line-clamp-1 flex-1 text-center">
              {{ email }}
            </div>
            <IconPaperAirplane
              class="size-4 text-primary"
            />
            <BaseInnerLoading
              v-if="currentlySendingTo === email"
            />
          </button>
        </li>
      </ul>

      <button
        type="button"
        class="flex w-full items-center justify-center py-2 font-semibold text-primary"
        @click="isAlternateAddressInputVisible = true"
      >
        <IconPlus class="mr-2" />
        Add Email Address
      </button>
    </div>
  </BaseDrawer>
</template>

<script setup lang="ts">
import { usePortalEmailAddresses } from "@/services/listPortalEmailAddresses"
import { requestPortalLoginLink } from "@/services/requestPortalLoginLink"

const {
  reveal,
  isRevealed: isOpen,
  confirm,
  cancel,
} = useConfirmDialog()

const isAlternateAddressInputVisible = ref(false)
const currentlySendingTo = ref<string | null>(null)

const {
  data: emailAddresses,
  isError,
  isLoading,
} = usePortalEmailAddresses()

const requestLoginLink = async (email: string) => {
  currentlySendingTo.value = email
  try {
    await requestPortalLoginLink(email)
    handleEmailSent(email)
  } catch {
    toast.error("Failed to send access link")
  } finally {
    currentlySendingTo.value = null
  }
}

const handleEmailSent = (email: string) => {
  toast.success(`Sent to ${email}`)
  isAlternateAddressInputVisible.value = false
  confirm()
}

const handleCloseDrawer = () => {
  isAlternateAddressInputVisible.value = false
  cancel()
}

defineExpose({
  reveal,
})
</script>
